import React, { useEffect, useMemo, useState } from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress'
import Link from '@material-ui/core/Link'
import { makeStyles } from '@material-ui/core/styles'
import DashboardIcon from '@material-ui/icons/ViewQuilt'
import moment from 'moment'
import { isNumber } from 'lodash'
import numbro from 'numbro'
import { capitalCase } from 'change-case'
import { getModalityMetrics } from '../../modalities-metrics-dashboard/config/modalityConfigs'
import * as api from '../../data'

const SEVERITY = {
  critical: { rank: 0, label: 'Critical', color: '#cf222e', bg: '#ffe9e8' },
  offline: { rank: 1, label: 'Offline', color: '#57606a', bg: '#eceef1' },
  warning: { rank: 2, label: 'Warning', color: '#9a6700', bg: '#fdf3d7' }
}
const HEALTHY = { color: '#1a7f37' }

const GROUPS = [
  { key: 'MRI', label: 'MRI', match: (m) => m.modality === 'MRI' },
  {
    key: 'CT',
    label: 'CT / PET-CT',
    match: (m) => m.modality === 'CT' || m.modality === 'PETCT'
  }
]

// Same semantics as the old countDeviceStatus: any non-green timeStatusColor
// means the device is not reporting, regardless of its last health reading.
const severityOf = (item) => {
  if (item.timeStatusColor !== 'green') return 'offline'
  if (item.healthStatusColor === 'red') return 'critical'
  if (item.healthStatusColor === 'yellow') return 'warning'
  return 'healthy'
}

const formatValue = (value, unit) => {
  let v = value
  if (isNumber(v)) {
    v = numbro(v).format({
      thousandSeparated: true,
      mantissa: Number.isInteger(v) ? 0 : 2
    })
  }
  if (!unit) return `${v}`
  return unit === '%' ? `${v}%` : `${v} ${unit}`
}

// One chip per metric in healthStatusMatches.red / .yellow, labeled and
// unit-annotated from the shared modality metrics config.
const issueChips = (item) => {
  const metrics = getModalityMetrics(item.modality, item.manufacturer)
  const chips = []

  const push = (keys, severity) =>
    (keys || []).forEach((key) => {
      const cfg = metrics.find((m) => m.key === key)
      chips.push({
        key: `${severity}-${key}`,
        severity,
        text: `${cfg ? cfg.label : capitalCase(key)} ${formatValue(item[key], cfg && cfg.unit)}`
      })
    })

  push(item.healthStatusMatches && item.healthStatusMatches.red, 'critical')
  push(item.healthStatusMatches && item.healthStatusMatches.yellow, 'warning')

  if (severityOf(item) === 'offline') {
    const time = item[item.timeStatusKeys]
    const duration = time ? moment(time, 'M/DD/YYYY h:mm a').fromNow(true) : 'unknown time'
    chips.unshift({ key: 'offline', severity: 'offline', text: `No data for ${duration}` })
  }

  return chips
}

const useStyles = makeStyles({
  summaryCard: { padding: '14px 16px', height: '100%' },
  summaryHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 10
  },
  hbar: {
    display: 'flex',
    height: 14,
    borderRadius: 5,
    overflow: 'hidden',
    '& span': { display: 'block', borderRadius: 3, marginRight: 2 },
    '& span:last-child': { marginRight: 0 }
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 10,
    fontSize: 12,
    color: '#4b545f'
  },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  swatch: { width: 8, height: 8, borderRadius: 2, display: 'inline-block' },
  pill: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderRadius: 999,
    padding: '2px 9px',
    whiteSpace: 'nowrap'
  },
  queueHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    margin: '43px 0 8px'
  },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterBtn: {
    border: '1px solid #d9dde3',
    background: '#fff',
    color: '#4b545f',
    borderRadius: 999,
    padding: '3px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },
  filterOn: { background: '#14181f', color: '#fff', borderColor: '#14181f' },
  row: {
    display: 'flex',
    alignItems: 'stretch',
    cursor: 'pointer',
    marginBottom: 8,
    overflow: 'hidden', // clips the square severity stripe to the Card's rounded corners
    '&:hover': { boxShadow: '0 2px 8px rgba(20,24,31,0.15)' }
  },
  stripe: { width: 4, flexShrink: 0 },
  rowBody: {
    flexGrow: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: '78px minmax(200px, 260px) 1fr minmax(130px, 170px)',
    gap: '4px 16px',
    alignItems: 'center',
    padding: '10px 16px'
  },
  rowId: { minWidth: 0 },
  site: { fontWeight: 600, fontSize: 13.5 },
  modTag: {
    display: 'inline-block',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: '#7a838f',
    border: '1px solid #d9dde3',
    borderRadius: 4,
    padding: '0 5px',
    marginLeft: 7,
    verticalAlign: 1
  },
  device: { color: '#4b545f', fontSize: 12, marginTop: 1 },
  serial: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', color: '#7a838f' },
  meta: { minWidth: 0, textAlign: 'right', fontSize: 11.5, color: '#7a838f' },
  open: { color: '#1d5eae', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  chips: { minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 6, alignContent: 'center' },
  chip: {
    display: 'inline-block',
    border: '1px solid #d9dde3',
    background: '#eef0f3',
    borderRadius: 7,
    padding: '3px 9px',
    fontSize: 12,
    color: '#4b545f',
    whiteSpace: 'nowrap'
  },
  empty: { padding: 24, textAlign: 'center', color: '#4b545f' }
})

const Pill = ({ severity }) => {
  const classes = useStyles()
  const s = SEVERITY[severity]
  return (
    <span className={classes.pill} style={{ color: s.color, background: s.bg }}>
      {s.label}
    </span>
  )
}

const SummaryCard = ({ label, counts }) => {
  const classes = useStyles()
  const segments = [
    { key: 'healthy', color: HEALTHY.color, value: counts.healthy },
    { key: 'warning', color: SEVERITY.warning.color, value: counts.warning },
    { key: 'critical', color: SEVERITY.critical.color, value: counts.critical },
    { key: 'offline', color: SEVERITY.offline.color, value: counts.offline }
  ]

  return (
    <Card className={classes.summaryCard}>
      <div className={classes.summaryHead}>
        <Typography style={{ fontSize: 13, fontWeight: 600 }}>
          {label} — {counts.total} unit{counts.total === 1 ? '' : 's'}
        </Typography>
        {counts.critical > 0 && <Pill severity="critical" />}
      </div>
      <div
        className={classes.hbar}
        role="img"
        aria-label={`${label}: ${counts.healthy} healthy, ${counts.warning} warning, ${counts.critical} critical, ${counts.offline} offline`}
      >
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <span key={s.key} style={{ flexGrow: s.value, background: s.color }} />
          ))}
      </div>
      <div className={classes.legend}>
        {segments.map((s) => (
          <span key={s.key} className={classes.legendItem}>
            <span className={classes.swatch} style={{ background: s.color }} />
            <b style={{ color: '#14181f' }}>{s.value}</b> {s.key}
          </span>
        ))}
      </div>
    </Card>
  )
}

const AttentionRow = ({ item }) => {
  const classes = useStyles()
  const severity = severityOf(item)
  const chips = issueChips(item)
  const lastReport = item[item.timeStatusKeys]

  return (
    <Card
      className={classes.row}
      onClick={() => (window.location.href = `/#/modalities/${item.id}/show`)}
    >
      <div className={classes.stripe} style={{ background: SEVERITY[severity].color }} />
      <div className={classes.rowBody}>
        <Pill severity={severity} />
        <div className={classes.rowId}>
          <div className={classes.site}>
            {item.siteName}
            <span className={classes.modTag}>{item.modality}</span>
          </div>
          <div className={classes.device}>
            {item.manufacturer} {item.model} · <span className={classes.serial}>SN {item.serialNumber}</span>
          </div>
        </div>
        <div className={classes.chips}>
          {chips.map((chip) => {
            const s = SEVERITY[chip.severity]
            return (
              <span
                key={chip.key}
                className={classes.chip}
                style={
                  chip.severity === 'offline'
                    ? undefined
                    : { color: s.color, background: s.bg, borderColor: s.color }
                }
              >
                {chip.text}
              </span>
            )
          })}
        </div>
        <div className={classes.meta}>
          {lastReport && <div>last report {lastReport}</div>}
          <div className={classes.open}>Open device →</div>
        </div>
      </div>
    </Card>
  )
}

const SEVERITY_KEYS = Object.keys(SEVERITY)

const ModalityDashboard = () => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [modalityFilter, setModalityFilter] = useState('ALL')
  const [severityFilter, setSeverityFilter] = useState({
    critical: true,
    offline: false,
    warning: true
  })

  useEffect(() => {
    ;(async () => {
      const result = await api.modalities.fetch({ filter: '{"active":1}' })
      setData(result || [])
    })()
  }, [])

  const groups = useMemo(() => {
    return GROUPS.map((group) => {
      const items = (data || []).filter(group.match)
      const counts = { total: items.length, healthy: 0, warning: 0, critical: 0, offline: 0 }
      items.forEach((item) => {
        counts[severityOf(item)] += 1
      })
      return { ...group, items, counts }
    })
  }, [data])

  const impacted = useMemo(() => {
    return groups
      .filter((g) => modalityFilter === 'ALL' || g.key === modalityFilter)
      .flatMap((g) => g.items)
      .filter((item) => {
        const severity = severityOf(item)
        return severity !== 'healthy' && severityFilter[severity]
      })
      .sort(
        (a, b) =>
          SEVERITY[severityOf(a)].rank - SEVERITY[severityOf(b)].rank ||
          (a.siteName || '').localeCompare(b.siteName || '')
      )
  }, [groups, modalityFilter, severityFilter])

  if (data === null) {
    return (
      <Box mt={2} width="100%">
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box mt={2} mb={4} width="100%">
      <Grid container spacing={1}>
        {groups.map((group) => (
          <Grid item xs={12} md={6} key={group.key}>
            <SummaryCard label={group.label} counts={group.counts} />
          </Grid>
        ))}
      </Grid>

      <div className={classes.queueHead}>
        <Typography style={{ fontSize: 14, fontWeight: 600 }}>
          Needs attention <span style={{ color: '#7a838f', fontWeight: 400 }}>· {impacted.length} device{impacted.length === 1 ? '' : 's'}</span>
        </Typography>
        <div className={classes.filters}>
          {['ALL', 'MRI', 'CT'].map((key) => (
            <button
              key={key}
              className={`${classes.filterBtn} ${modalityFilter === key ? classes.filterOn : ''}`}
              onClick={() => setModalityFilter(key)}
            >
              {key === 'ALL' ? 'All' : key}
            </button>
          ))}
          <span style={{ width: 8 }} />
          {SEVERITY_KEYS.map((key) => (
            <button
              key={key}
              className={`${classes.filterBtn} ${severityFilter[key] ? classes.filterOn : ''}`}
              onClick={() => setSeverityFilter({ ...severityFilter, [key]: !severityFilter[key] })}
            >
              {SEVERITY[key].label}
            </button>
          ))}
        </div>
      </div>

      {impacted.length === 0 ? (
        <Card className={classes.empty}>
          <Typography>
            {(() => {
              const activeLabels = SEVERITY_KEYS.filter((key) => severityFilter[key]).map((key) =>
                SEVERITY[key].label.toLowerCase()
              )
              if (activeLabels.length === 0) return 'Select a severity filter to see devices.'
              if (activeLabels.length === SEVERITY_KEYS.length && modalityFilter === 'ALL') {
                return `All ${(data || []).filter((m) => GROUPS.some((g) => g.match(m))).length} devices healthy.`
              }
              return `No ${activeLabels.join(' or ')} devices right now.`
            })()}
          </Typography>
        </Card>
      ) : (
        impacted.map((item) => <AttentionRow key={item.id} item={item} />)
      )}

      <Typography style={{ fontSize: 12.5, color: '#7a838f', margin: '10px 4px' }}>
        Healthy devices and fleet inventory:{' '}
        <Link href="/#/modalities" style={{ fontWeight: 600 }}>
          All modalities →
        </Link>
      </Typography>
    </Box>
  )
}

export default ModalityDashboard
export const ModalityDashboardIcon = DashboardIcon
